<template>
    <div class="coupon-redeem">
        <h2 class="coupon-redeem__title">{{ getTranslatedText('extra7 boost freebet coupon') }}</h2>

        <div class="coupon-redeem__input">
            <label>{{ getTranslatedText('coupon code') }}</label>
            <div class="coupon-redeem__input__content">
                <input
                    v-model="code"
                    type="text"
                    :placeholder="getTranslatedText('scan coupon')"
                    :disabled="lockedCode"
                    :class="{ 'is-locked': lockedCode }"
                    @keyup.enter="onValidate"
                />
            </div>
        </div>

        <div class="coupon-redeem__box-button">
            <button
                class="coupon-redeem__btn coupon-redeem__btn--primary"
                :disabled="isLoading || code.trim() === ''"
                @click="onValidate"
            >
                <span v-if="!isLoading">{{ getTranslatedText('search') }}</span>
                <span v-else>...</span>
            </button>
        </div>

        <div v-if="coupon" class="coupon-redeem__panel">
            <div class="coupon-redeem__row coupon-redeem__row--head">
                {{ getTranslatedText('extra7 boost') }}<template v-if="coupon.boost_category"> - {{ coupon.boost_category }}</template>
            </div>
            <div class="coupon-redeem__row">
                <span>{{ getTranslatedText('status') }}</span>
                <span :class="['coupon-redeem__status', isValid ? 'is-active' : 'is-inactive']">
                    {{ getTranslatedText(coupon.status || '') }}
                </span>
            </div>
            <div class="coupon-redeem__row">
                <span>{{ getTranslatedText('remaining spins') }}</span>
                <span class="bold">{{ coupon.freebet_quantity }}</span>
            </div>
            <div class="coupon-redeem__row">
                <span>{{ getTranslatedText('freebet amount') }}</span>
                <span class="bold">{{ renderPrice(coupon.freebet_amount) }}</span>
            </div>
            <div class="coupon-redeem__row">
                <span>{{ getTranslatedText('expires at') }}</span>
                <span>{{ coupon.expires_at ? renderDate(coupon.expires_at) : 'NA' }}</span>
            </div>

            <div class="coupon-redeem__actions">
                <button
                    class="coupon-redeem__btn coupon-redeem__btn--primary"
                    :disabled="!isValid"
                    @click="onPlayNow"
                >
                    {{ getTranslatedText('play now') }}
                </button>
                <button
                    class="coupon-redeem__btn coupon-redeem__btn--ghost"
                    @click="onPrintCoupon"
                >
                    {{ getTranslatedText('print coupon') }}
                </button>
            </div>
        </div>

        <CouponPrintStatus />
    </div>
</template>
<script>
import EventBus from '@/utils/EventBus'
import languageMixins from '@/mixins/language/language-mixins'
import printMixins from '@/mixins/print/print-mixin'
import CouponService from '@/services/coupon.service'
import CouponPrintStatus from '@/components/common/dialog/coupon-print-status.vue'
import { formatPrice, formatDateUTC00 } from '@/utils/utils'

export default {
    mixins: [languageMixins, printMixins],
    components: { CouponPrintStatus },
    data() {
        return {
            code: '',
            coupon: null,
            isLoading: false,
            // Locked when the code arrives from a scan / manual entry on the Scan
            // page — it's not editable here, only on the Scan screen.
            lockedCode: false,
        }
    },
    computed: {
        isValid() {
            return !!(this.coupon && this.coupon.valid && parseInt(this.coupon.freebet_quantity, 10) > 0)
        },
    },
    mounted() {
        const queryCode = this.$route.query && this.$route.query.code ? String(this.$route.query.code) : ''
        if (queryCode) {
            this.code = queryCode.trim()
            this.lockedCode = true
            this.onValidate()
        }
    },
    methods: {
        renderPrice(value) {
            return formatPrice(parseFloat(value || 0), 0)
        },
        renderDate(date) {
            if (!date) return ''
            try {
                const d = formatDateUTC00(date)
                return `${d.day}/${d.month}/${d.year} ${d.hour}:${d.minutes}`
            } catch (e) {
                return date
            }
        },
        showNotification(text, type) {
            EventBus.$emit('show-notications', { text, type })
        },
        async onValidate() {
            if (this.isLoading) return
            const code = this.code.trim()
            if (code === '') return
            this.isLoading = true
            this.coupon = null
            try {
                const body = await CouponService.validate(code)
                const info = body && body.data ? body.data : body
                if (!info || (body && body.status && body.status !== 200)) {
                    this.showNotification(this.getTranslatedText('coupon not found'), 'error')
                    this.isLoading = false
                    return
                }
                this.coupon = info
                if (!this.isValid) {
                    // Expired coupon (status flipped server-side once past expires_at):
                    // show the specific expiry message instead of the generic one.
                    const status = (info.status || '').toString().toLowerCase()
                    if (status === 'expired') {
                        this.showNotification(this.getTranslatedText('freebet coupon expired'), 'error')
                    } else {
                        this.showNotification(this.getTranslatedText('coupon not valid'), 'error')
                    }
                }
            } catch (error) {
                console.log(error)
                this.showNotification(this.getTranslatedText('coupon not found'), 'error')
            }
            this.isLoading = false
        },
        onPlayNow() {
            if (!this.isValid) return
            // A freebet play takes over the Loto 5/90 betslip — clear any pending 5/90
            // bets automatically so the freebet basket starts clean, then move forward.
            this.$store.state.stepCart = 1
            this.$store.dispatch('onClearCart')
            this.$store.commit('SET_FREEBET_CONTEXT', {
                code: this.coupon.code,
                remaining: parseInt(this.coupon.freebet_quantity, 10) || 0,
                amount: parseFloat(this.coupon.freebet_amount) || 0,
                category: this.coupon.boost_category || null,
            })
            this.$router.push('/loto')
        },
        onPrintCoupon() {
            if (!this.coupon) return
            // Route through the isolated coupon-print dialog (printer ack + reprint
            // on failure) instead of a raw print postMessage.
            EventBus.$emit('on-show-coupon-print', {
                code: this.coupon.code,
                freebet_quantity: this.coupon.freebet_quantity,
                freebet_amount: this.coupon.freebet_amount,
                currency_code: this.$store.state.userInfo?.gamev3?.currency_code || '',
                created_at: this.coupon.created_at,
                expires_at: this.coupon.expires_at,
                source_ticket_id: this.coupon.source_transaction_reference || '',
            })
        },
    },
}
</script>
<style lang="scss">
.coupon-redeem {
    padding: 16px;
    max-width: 520px;
    margin: 0 auto;
}
.coupon-redeem__title {
    text-align: center;
    margin-bottom: 16px;
}
.coupon-redeem__input {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 12px;
}
.coupon-redeem__input__content input {
    width: 100%;
    padding: 12px;
    border: 1px solid #ccc;
    border-radius: 8px;
    font-size: 18px;
    box-sizing: border-box;
    text-transform: uppercase;
}
.coupon-redeem__input__content input.is-locked,
.coupon-redeem__input__content input:disabled {
    background-color: #ececec;
    color: #555;
    cursor: not-allowed;
}
.coupon-redeem__box-button {
    margin-bottom: 16px;
}
.coupon-redeem__btn {
    width: 100%;
    padding: 12px;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
}
.coupon-redeem__btn--primary {
    background-color: #1a7f37;
    color: #fff;
}
.coupon-redeem__btn--primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
.coupon-redeem__btn--ghost {
    background-color: #fff;
    color: #1a7f37;
    border: 1px solid #1a7f37;
}
.coupon-redeem__panel {
    border: 1px solid #f0c46b;
    border-radius: 10px;
    padding: 14px;
    background-color: #fffaf0;
}
.coupon-redeem__row {
    display: flex;
    justify-content: space-between;
    padding: 6px 0;
}
.coupon-redeem__row--head {
    justify-content: center;
    font-weight: 800;
    font-size: 18px;
    color: #8a5a00;
    border-bottom: 1px dashed #f0c46b;
    margin-bottom: 6px;
}
.coupon-redeem__row .bold {
    font-weight: 700;
}
.coupon-redeem__status.is-active {
    color: #1a7f37;
    font-weight: 700;
}
.coupon-redeem__status.is-inactive {
    color: #b00020;
    font-weight: 700;
}
.coupon-redeem__actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 14px;
}
</style>
